import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core'
import { CommentaryEntityContext } from '@helios/common-api'
import { CommentaryEntityRef, CommentaryEntityType } from '../types/thread-jump'
import { CommentaryEntitySearchService } from '../services/commentary-entity-search.service'
import { CommentaryGridSelectionService } from '../services/commentary-grid-selection.service'

/**
 * The two function props the commentary-ui web component consumes for the
 * entity-link UX. They CANNOT go through Angular attribute bindings (the
 * `[attr.*]` path the string props use) — DOM attributes are strings, so a
 * function would be stringified. They must be set as JS PROPERTIES directly
 * on the element. We grab the element via a ViewChild setter (it mounts /
 * unmounts under *ngIf) and assign these bound class fields imperatively.
 */
type EntitySearchFn = (
  query: string,
  type?: CommentaryEntityType,
) => Promise<CommentaryEntityRef[]>
type PickEntitiesFn = (
  type?: CommentaryEntityType,
) => Promise<CommentaryEntityRef[]>

@Component({
  selector: 'hs-comments-drawer',
  templateUrl: './comments-drawer.component.html',
  styleUrls: ['./comments-drawer.component.scss'],
})
export class CommentsDrawerComponent {
  @Output() closePanel = new EventEmitter<void>()
  @Input() isVisible = false
  @Input() entityContext: CommentaryEntityContext | null = null
  @Input() commentaryAction: 'view' | 'add' | null = null
  /** See actionSeq in commentary-ui's web-component-config. */
  @Input() commentaryActionSeq = 0
  @Input() accountId: string | null = null
  /** JSON-encoded string[], forwarded as the related-account-ids attribute. */
  @Input() relatedAccountIds: string | null = null
  /** Forwarded as `user-id` to the React drawer for `X-Dev-User` attribution in local dev. In beta/prod the bearer's `sub` is authoritative. */
  @Input() userId: string | null = null
  /** Display name forwarded as `user-display-name` / `X-User-Display`. Display-only — audit `userId` stays bearer-driven. */
  @Input() userDisplayName: string | null = null
  /**
   * DLITE-4369 — the specific comment to jump to, from a shared comment
   * link. Deliberately separate from `entityContext`: entityContext PINS an
   * entity for compose/view, but a single entity can have several threads,
   * so it cannot address one specific comment. (threadIds, below, is the
   * thread half of the same link — see threadIdsJson's doc for why it's an
   * array rather than a matching scalar.)
   */
  @Input() commentId: string | null = null
  @Input() threadIds: string[] | null = null
  /** See threadIdsSeq in commentary-ui's web-component-config. */
  @Input() threadIdsSeq = 0

  /**
   * Stringified because commentary-ui declares the prop as r2wc `string`, an
   * identity transform: the array itself reaches the DOM as `"a,b"`, which
   * parses to no filter and silently shows the whole account. `'[]'` when
   * empty, for the reason entityContextAttr gives about falsy updates.
   *
   * DLITE-4369 — commentary-ui takes this as an array (not a scalar
   * `thread-id`) because a deep link is the one-id case of the same slice a
   * widget click narrows to; a scalar prop couldn't represent both.
   */
  get threadIdsJson(): string {
    return JSON.stringify(this.threadIds ?? [])
  }

  /** The account the drawer actually reports to commentary-ui. */
  get effectiveAccountId(): string | null {
    return this.entityContext?.accountId ?? this.accountId ?? null
  }

  /**
   * `relatedAccountIds` is resolved against `accountId`, but the drawer reports
   * `effectiveAccountId`. The two diverge while a deep link is pending —
   * `clearCommentaryContext()` no-ops then, so an account change re-points `accountId`
   * while `entityContext` stays pinned to the link's account. Sending the new account's
   * subaccounts under the pinned account's id would roll up unrelated threads, so drop
   * the rollup instead; the drawer falls back to the pinned account's own threads.
   *
   * Returns "[]" and NEVER null — the same canonical-empty contract `threadIdsAttr` below
   * follows. r2wc skips falsy attribute updates, so `[attr.x]="null"` (a removeAttribute)
   * leaves the React prop on its previous value: clearing with null clears nothing, and the
   * divergence guard above silently stops working. "[]" is what reaches commentary-ui as
   * "roll nothing up".
   */
  get relatedAccountIdsAttr(): string {
    // String-compared, not ===: entityContext.accountId is declared string but is populated
    // from sources TypeScript does not reach (a React CustomEvent detail, a query param), and
    // app.component coerces it with String() at its own boundary for that reason. A numeric
    // 123 against a "123" here would read as divergence and silently drop the rollup.
    const pinned = this.entityContext?.accountId
    const pinnedIsThisAccount =
      pinned == null || String(pinned) === String(this.accountId)
    return pinnedIsThisAccount ? this.relatedAccountIds ?? '[]' : '[]'
  }

  /**
   * DLITE-3908: the entity-link labels as a JSON string for the
   * `ref-descriptions` attribute. commentary-ui declares the prop as r2wc
   * `json`, so it parses this string back into an object. We use the attribute
   * path (not a JS property like entitySearch/pickEntities) because r2wc
   * observes declared attributes and re-applies them on element upgrade —
   * sidestepping the mount-before-define race that functions have to guard
   * against. Returns null when there are no labels so the attribute stays unset.
   */
  get entityDescriptionsJson(): string | null {
    const descriptions = this.entityContext?.entityDescriptions
    return descriptions && Object.keys(descriptions).length > 0
      ? JSON.stringify(descriptions)
      : null
  }

  constructor(
    private readonly entitySearchService: CommentaryEntitySearchService,
    private readonly gridSelectionService: CommentaryGridSelectionService,
  ) {}

  private commentaryElRef?: ElementRef<HTMLElement & { restore?: () => void }>

  /**
   * The element mounts/unmounts with *ngIf, so a ViewChild SETTER (rather
   * than a static query) catches it each time it appears and re-applies the
   * function props. Assigning the same stable bound fields every time is
   * harmless — r2wc only re-renders when a prop value CHANGES identity, and
   * these never do.
   */
  @ViewChild('commentaryEl')
  set commentaryEl(ref: ElementRef<HTMLElement> | undefined) {
    this.commentaryElRef = ref
    const el = ref?.nativeElement as
      | (HTMLElement & {
          entitySearch?: EntitySearchFn
          pickEntities?: PickEntitiesFn
        })
      | undefined
    if (el == null) return
    // r2wc only forwards a property to React once <commentary-ui> has been
    // UPGRADED (its prototype prop accessors exist). The drawer can mount via
    // *ngIf before commentary-ui.js finishes loading and calls
    // customElements.define(); assigning then creates an instance-own property
    // that permanently shadows the accessor, so entitySearch/pickEntities never
    // reach React and the entity picker silently degrades to free-form id
    // entry. Gate on whenDefined so the assignment always lands on the upgraded
    // element. (When already defined, whenDefined resolves on the next
    // microtask — imperceptible, as the picker only renders on "Link more".)
    void customElements.whenDefined('commentary-ui').then(() => {
      el.entitySearch = this.entitySearch
      el.pickEntities = this.pickEntities
    })
  }

  /**
   * DLITE-4743 — commentary-ui hides its entire root on minimize with no
   * in-drawer control left to bring it back, and exposes this `restore()`
   * method directly on its own custom element as the only way back (see its
   * own comment for why that's a plain instance method rather than a prop).
   * Forwarded here so the frame, and ultimately app.component's own
   * "Comments" toggle, can reach it without knowing anything about
   * commentary-ui's internals. Returns whether the call landed: a
   * separately-deployed commentary-ui build may not expose `restore` at all.
   */
  restore(): boolean {
    const el = this.commentaryElRef?.nativeElement
    if (typeof el?.restore !== 'function') return false
    el.restore()
    return true
  }

  /**
   * Serializes the bulk suspense link handles for the `suspense-ids`
   * attribute. commentary-ui consumes a JSON-encoded `string[]` here; returns
   * `null` (attribute omitted) when there are no bulk handles, so a single-row
   * link falls through to the `suspense-id` attribute instead.
   */
  get suspenseIdsAttr(): string | null {
    const ids = this.entityContext?.suspenseIds
    return ids && ids.length ? JSON.stringify(ids) : null
  }

  /**
   * The pinned-entity context as a single JSON string, or the literal string
   * `"null"` when nothing is pinned. commentary-ui consumes this as its
   * authoritative `entity-context` prop.
   *
   * We must emit `"null"` (a truthy string) rather than rely on clearing the
   * individual entity attributes: r2wc ignores falsy attribute updates, so
   * removing transaction-id/asset-id can't reset the component's pin in place.
   * A `json` attribute set to "null" is forwarded and parsed to null, clearing
   * the pin without re-mounting the drawer (which would drop the user's
   * in-progress draft and reload the thread list).
   */
  get entityContextAttr(): string {
    const c = this.entityContext
    if (!c) return 'null'
    return JSON.stringify({
      transactionId: c.transactionId,
      extTranId: c.extTranId,
      breakId: c.breakId,
      assetId: c.assetId,
      suspenseId: c.suspenseId,
      suspenseIds: this.suspenseIdsAttr ?? undefined,
    })
  }

  /**
   * `entitySearch` — type-ahead over the rows already loaded in the visible
   * grids. Bound class field so its identity is stable across change
   * detection (r2wc would otherwise thrash on a new closure each cycle).
   */
  readonly entitySearch: EntitySearchFn = (query, type) =>
    this.entitySearchService.search(query, type)

  /**
   * `pickEntities` — the currently-focused grid's current selection. The
   * service returns synchronously; we wrap in a resolved Promise to honour
   * the web-component contract `(type?) => Promise<EntityRef[]>`.
   */
  readonly pickEntities: PickEntitiesFn = type =>
    Promise.resolve(this.gridSelectionService.collectSelection(type))
}
